import type { PropType } from 'vue';
import type { TThemeOverride } from '../../theme/utils';
import { computed, defineComponent, h, provide } from 'vue';
import { injectionKey } from '../../composables/theme';
import { createTheme } from '../../theme/utils';

/**
 * Props for `<TThemeProvider>` — root component that injects design tokens for a subtree.
 *
 * ## When to use
 *
 * Wrap any subtree that needs a different brand color than the library default
 * (Toku purple). Typical placements:
 *
 * - **App root**: matches one brand for the entire app (e.g. Toku's webapp uses defaults).
 * - **Per-tenant subtree**: multi-tenant white-label apps inject the tenant's brand
 *   per request (e.g. payment-portal-frontend wraps each tenant's view).
 *
 * Without a `<TThemeProvider>`, components render with the default brand from
 * `toku-ui/tokens.css`. With one, the wrapped subtree gets a regenerated brand ramp.
 *
 * ## Basic usage
 *
 * ```vue
 * <script setup>
 * import { TThemeProvider } from 'toku-ui';
 * </script>
 *
 * <template>
 *   <TThemeProvider :theme="{ colors: { brand: { base: '#FF6600' } } }">
 *     <App />
 *   </TThemeProvider>
 * </template>
 * ```
 *
 * ## How it works
 *
 * The provider renders a `<div class="toku-theme">` wrapper with the brand-derived CSS
 * variables set inline. Descendant components reading `var(--color-bg-primary-default)`
 * (and every other brand-derived semantic) resolve to the new brand color via standard
 * CSS-variable cascade. **Zero Vue re-renders** — only browser CSS recalc.
 */
export interface TThemeProviderProps {
  /**
   * Theme override. Pass `{ colors: { brand: { base: '#hex' } } }` (canonical) or the
   * legacy `{ colors: { primary: { main: '#hex' } } }` shape. Both regenerate the
   * 12-step brand ramp + alpha twin and propagate via CSS variables to descendants.
   *
   * Omit the prop entirely to use the library defaults (Toku purple).
   *
   * @example
   * ```vue
   * <!-- Tenant-specific brand -->
   * <TThemeProvider :theme="{ colors: { brand: { base: '#FF6600' } } }">
   *   <PaymentForm />
   * </TThemeProvider>
   * ```
   */
  theme?: TThemeOverride;
}

export const TThemeProvider = defineComponent({
  name: 'TThemeProvider',
  inheritAttrs: false,
  props: {
    theme: {
      type: Object as PropType<TThemeProviderProps['theme']>,
      default: () => ({}),
    },
  },
  setup(props, { slots, attrs }) {
    const theme = computed(() => createTheme(props.theme ?? {}));

    provide(injectionKey, theme);

    return () => {
      const { cssVars } = theme.value;
      // Pass class + style as arrays so Vue normalizes every supported binding
      // shape (string, array, or object). Spreading attrs.style would break when
      // it's a string ('display:flex' → { 0: 'd', 1: 'i', ... }); same risk for class.
      return h(
        'div',
        {
          ...attrs,
          class: ['toku-theme', attrs.class],
          style: cssVars ? [attrs.style, cssVars] : attrs.style,
        },
        slots.default?.(),
      );
    };
  },
});
